import { Alert, Button, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { bbox } from "@turf/bbox";
import type {
	Feature,
	FeatureCollection,
	MultiPolygon,
	Polygon,
} from "geojson";
import type { LatLngBoundsLiteral, Map as LeafletMap } from "leaflet";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useDrawPolygons } from "src/hooks/useDrawPolygon";
import type { APIError } from "src/types";
import { featureToWKT } from "src/utils/geometry.utils";

interface Props {
	map: LeafletMap;
	isLoading: boolean;
	errors?: APIError[] | null;
	onCancel: () => void;
	initialPolygons: Feature<Polygon | MultiPolygon>[];
	warningList: string[];
	onSubmit: (polygonList_frontendSrid: string[]) => void;
	clearPolygons: () => void;
	clearWarningList: () => void;
}

export function CaseCreatePredefinedForm(props: Props) {
	const { initialPolygons, clearWarningList } = props;
	const { t } = useTranslation();
	const {
		polygons,
		startDrawing,
		redrawDrawing: deleteDrawing,
		editDrawing,
		stopDrawing,
		renderInitialPolygons,
	} = useDrawPolygons(props.map);

	useEffect(() => {
		if (!polygons && initialPolygons.length > 0) {
			renderInitialPolygons(initialPolygons);
			const featureCollection: FeatureCollection = {
				type: "FeatureCollection",
				features: initialPolygons,
			};
			const bounds = bbox(featureCollection);
			const boundsLatLng: LatLngBoundsLiteral = [
				[bounds[1], bounds[0]],
				[bounds[3], bounds[2]],
			];
			props.map.fitBounds(boundsLatLng, {
				padding: [100, 100],
			});
		}
	}, [initialPolygons, polygons, renderInitialPolygons, props.map]);

	useEffect(() => {
		if (!polygons && initialPolygons.length === 0) {
			startDrawing();
		} else {
			stopDrawing();
		}
	}, [polygons, startDrawing, stopDrawing, initialPolygons.length]);

	function handleSubmit() {
		if (!polygons) return;
		const polygonList_frontendSrid = polygons.map((polygon) =>
			featureToWKT(polygon),
		);
		props.onSubmit(polygonList_frontendSrid);
	}
	return render();

	function render() {
		return (
			<Styles>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h4" color="primary">
							{t("title.createCase")}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						{polygons ? (
							<Typography>
								<Trans i18nKey="label.clickFinishToCreateYourCaseOrRedrawEditToEditThePolygonFirst" />{" "}
							</Typography>
						) : (
							<Typography>{t("label.drawRegionOnTheMap")}</Typography>
						)}
					</Grid>
					{polygons && (
						<Grid item xs={12}>
							<Button
								color="primary"
								onClick={() => {
									deleteDrawing();
									clearWarningList();
								}}
							>
								{t("label.redraw")}
							</Button>
							<Button
								color="primary"
								onClick={() => {
									editDrawing();
									clearWarningList();
								}}
							>
								{t("label.edit")}
							</Button>
						</Grid>
					)}
					{props.warningList.length > 0 && (
						<Grid item xs={12} className="warning-list">
							{props.warningList.map((warning) => (
								<Alert severity="warning" key={warning}>
									{warning}
								</Alert>
							))}
						</Grid>
					)}
					{props.errors && (
						<Grid item xs={12}>
							<ServerErrors data={props.errors} />
						</Grid>
					)}

					<ButtonGrid
						container
						item
						xs={12}
						sx={{ justifyContent: "flex-end" }}
					>
						<Button
							variant="text"
							onClick={props.onCancel}
							disabled={props.isLoading}
						>
							{t("label.previous")}
						</Button>
						<ButtonLoading
							color="primary"
							variant="contained"
							disabled={!polygons}
							onClick={handleSubmit}
							isLoading={props.isLoading}
						>
							{t("label.finish")}
						</ButtonLoading>
					</ButtonGrid>
				</Grid>
			</Styles>
		);
	}
}

const Styles = styled("div")`
  .warning-list {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
  }

  .error-item {
    width: 100%;
  }
`;
